/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */
import {
  useCreateCustomEventMutation,
  useDeleteCustomEventMutation,
  useGetCustomEventsQuery,
  useUpdateCustomEventMutation,
} from "../../../../store/api/admin/adminApiSlice";
import { useStoreDispatch } from "../../../../store/hooks";
import { useEffect, useState } from "react";
import { setBasicDialogOpenFor, setErrorMsg, setOpenToast } from "../../../../store/helpers/helperReducers";
import { customEventsColumns } from "../../../../components/Shared/Table/Columns";
import { ICustomEvent } from "../../../../store/api/admin/types";
import { errorMessage, titleCase } from "../../../../utils/helpers";
import { customEventPayload } from "../../../Configuration/helpers";
import { isError } from "../../../../utils/validations";

interface IPayload {
  [key: string]: string;
}

export const useDisplayCustomEvents = () => {
  const { data, isLoading, refetch } = useGetCustomEventsQuery();

  return {
    data,
    isLoading,
    customEventsColumns,
    refetch,
  };
};

export const useAddEditDeleteCustomEvent = (selectedCustomEventReceived?: ICustomEvent, isCreateCustomEventReceived?: boolean) => {
  const { data, refetch } = useDisplayCustomEvents();
  const dispatch = useStoreDispatch();

  const [errMsg, setErrMsg] = useState<IPayload>({});
  const [selectedCustomEvent, setSelectedCustomEvent] = useState<any>(selectedCustomEventReceived);
  const [formData, setFormData] = useState<ICustomEvent>(selectedCustomEventReceived!);
  const [isCreateCustomEvent, setisCreateCustomEvent] = useState(isCreateCustomEventReceived);

  const [deleteCustomEvent] = useDeleteCustomEventMutation();
  const [createCustomEvent] = useCreateCustomEventMutation();
  const [updateCustomEvent] = useUpdateCustomEventMutation();

  useEffect(() => {
    if (selectedCustomEventReceived) {
      setSelectedCustomEvent(selectedCustomEventReceived);
      setFormData(selectedCustomEventReceived);
    }
  }, [selectedCustomEventReceived]);

  useEffect(() => {
    if (typeof isCreateCustomEventReceived === "boolean") {
      setisCreateCustomEvent(isCreateCustomEventReceived);
    }
  }, [isCreateCustomEventReceived]);


  const handleDeleteCustomEvent = async (eventName: string, actionName: string) => {
    if (eventName) {
      try {
        let result: any;
        if (actionName.match("delete")) {
          result = await deleteCustomEvent(eventName);
          if (result?.error) throw result?.error;
          if (result.data?.status === 200) {
            dispatch(
              setOpenToast({
                openToast: true,
                toastType: "success",
                message: `Custom Event -  ${eventName} has been deleted .`,
              })
            );
            refetch();
          }
        }
      } catch (error) {
        dispatch(setOpenToast({ openToast: true, toastType: "error", message: errorMessage(error) }));
      }
    }
  };

  const handleValidateCustomEvent = () => {
    const nextErrors: IPayload = {};

    if (!formData?.name) {
      nextErrors.name = "Name is required";
    } else {
      const nameRegex: RegExp = /^[a-zA-Z0-9_]+$/;
      const isValidName = nameRegex.test(formData?.name);
      if (!isValidName) {
        nextErrors.name = "A custom event name should contain only alphanumeric characters or underscores.";
      }
    }

    setErrMsg(nextErrors);
    dispatch(setErrorMsg(nextErrors));

    return !isError(nextErrors);
  };

  const submitHandler = async () => {
    if (handleValidateCustomEvent()) {
      try {
        let result: any;
        const isCreateMode =
          typeof isCreateCustomEventReceived === "boolean"
            ? isCreateCustomEventReceived
            : !!isCreateCustomEvent;

        if (isCreateMode) {
          result = await createCustomEvent(customEventPayload(formData!));
        } else {
          result = await updateCustomEvent(customEventPayload(formData!));
        }
        if (result?.error) throw result?.error;
        if (result.data?.status === 200) {
          dispatch(setBasicDialogOpenFor(undefined));
          dispatch(
            setOpenToast({
              openToast: true,
              toastType: "success",
              message: `Custom Event has been ${isCreateMode ? "added" : "updated"} for ${titleCase(formData?.name || "")}.`,
            })
          );
          refetch();
        }
      } catch (error: any) {
        dispatch(setBasicDialogOpenFor(undefined));
        dispatch(setOpenToast({ openToast: true, toastType: "error", message: errorMessage(error) }));
      }
    }
  };

  return {
    formData,
    setFormData,
    errMsg,
    setErrMsg,
    submitHandler,
    isCreateCustomEvent,
    setisCreateCustomEvent,
    selectedCustomEvent,
    setSelectedCustomEvent,
    handleDeleteCustomEvent,
  };
};
